import { useState } from 'react'
import { useSettingsStore } from '@/stores/settings'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { 
  Settings, 
  Server, 
  Monitor, 
  FileText,
  AlertTriangle,
  Save,
  RefreshCw,
  RotateCcw
} from 'lucide-react'
import { toast } from 'sonner'


export function SystemSettings() {
  const { system, updateSystem, resetSettings, loading } = useSettingsStore()
  const [hasChanges, setHasChanges] = useState(false)
  const [formData, setFormData] = useState(system)

  const handleFieldChange = (field: keyof typeof system, value: any) => {
    const newData = { ...formData, [field]: value }
    setFormData(newData)
    setHasChanges(true)
  }

  const handleSave = () => {
    updateSystem(formData)
    setHasChanges(false)
    toast.success('System settings saved!')
  }

  const handleReset = () => {
    setFormData(system)
    setHasChanges(false)
  }

  const handleResetAllSettings = () => {
    if (confirm('Are you sure you want to reset all settings to their defaults? This action cannot be undone.')) {
      resetSettings()
      setFormData(system)
      setHasChanges(false)
      toast.success('All settings reset to defaults')
    }
  }



  const transportOptions = [
    { value: 'http', label: 'HTTP', description: 'Standard HTTP REST API' },
    { value: 'websocket', label: 'WebSocket', description: 'Real-time bidirectional communication' },
    { value: 'sse', label: 'Server-Sent Events', description: 'Server-to-client event streaming' },
    { value: 'stdio', label: 'Standard I/O', description: 'Process-based communication' }
  ]

  const monitoringIntervals = [
    { value: 10, label: '10 seconds' },
    { value: 30, label: '30 seconds' },
    { value: 60, label: '1 minute' },
    { value: 300, label: '5 minutes' },
    { value: 600, label: '10 minutes' }
  ]

  const logLimits = [
    { value: 100, label: '100 entries' },
    { value: 500, label: '500 entries' },
    { value: 1000, label: '1,000 entries' },
    { value: 5000, label: '5,000 entries' },
    { value: 10000, label: '10,000 entries' }
  ]

  return (
    <div className="space-y-6">
      {/* Server Defaults */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Server className="h-5 w-5" />
            <span>Server Defaults</span>
          </CardTitle>
          <CardDescription>
            Default settings for new server deployments
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Default Transport</Label>
              <Select 
                value={formData.default_server_transport} 
                onValueChange={(value) => handleFieldChange('default_server_transport', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select transport" />
                </SelectTrigger>
                <SelectContent>
                  {transportOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex flex-col">
                        <span>{option.label}</span>
                        <span className="text-xs text-muted-foreground">{option.description}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Monitoring Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Monitor className="h-5 w-5" />
            <span>Monitoring</span>
          </CardTitle>
          <CardDescription>
            Configure monitoring and logging behavior
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Default Monitoring Interval</Label>
              <Select 
                value={formData.default_monitoring_interval.toString()} 
                onValueChange={(value) => handleFieldChange('default_monitoring_interval', parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select interval" />
                </SelectTrigger>
                <SelectContent>
                  {monitoringIntervals.map((option) => (
                    <SelectItem key={option.value} value={option.value.toString()}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                How often to check server health and collect metrics
              </p>
            </div>

            <div className="space-y-2">
              <Label>Maximum Log Entries</Label>
              <Select 
                value={formData.max_log_entries.toString()} 
                onValueChange={(value) => handleFieldChange('max_log_entries', parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select limit" />
                </SelectTrigger>
                <SelectContent>
                  {logLimits.map((option) => (
                    <SelectItem key={option.value} value={option.value.toString()}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Maximum number of log entries to retain per server
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Editor & Workflow */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="h-5 w-5" />
            <span>Editor & Workflow</span>
          </CardTitle>
          <CardDescription>
            Configure editor behavior and workflow preferences
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex-1">
                <div className="font-medium">Auto-save Drafts</div>
                <div className="text-sm text-muted-foreground">
                  Automatically save draft configurations while editing
                </div>
              </div>
              <Switch
                checked={formData.auto_save_drafts}
                onCheckedChange={(checked) => handleFieldChange('auto_save_drafts', checked)}
              />
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex-1">
                <div className="font-medium">Confirmation Dialogs</div>
                <div className="text-sm text-muted-foreground">
                  Show confirmation dialogs for destructive actions
                </div>
              </div>
              <Switch
                checked={formData.confirmation_dialogs}
                onCheckedChange={(checked) => handleFieldChange('confirmation_dialogs', checked)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* System Summary */}
      <Card>
        <CardHeader>
          <CardTitle>System Summary</CardTitle>
          <CardDescription>
            Overview of your current system preferences
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 border rounded-lg">
                <div className="font-medium">Default Transport</div>
                <div className="text-sm text-muted-foreground">
                  {transportOptions.find(t => t.value === formData.default_server_transport)?.label}
                </div>
              </div>
              <div className="p-4 border rounded-lg">
                <div className="font-medium">Monitoring Interval</div>
                <div className="text-sm text-muted-foreground">
                  {monitoringIntervals.find(i => i.value === formData.default_monitoring_interval)?.label}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 border rounded-lg">
                <div className="font-medium">Max Log Entries</div>
                <div className="text-sm text-muted-foreground">
                  {logLimits.find(l => l.value === formData.max_log_entries)?.label}
                </div>
              </div>
              <div className="p-4 border rounded-lg">
                <div className="font-medium">Features Enabled</div>
                <div className="text-sm text-muted-foreground">
                  {[formData.auto_save_drafts, formData.confirmation_dialogs].filter(Boolean).length} of 2
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            <span>Danger Zone</span>
          </CardTitle>
          <CardDescription>
            Irreversible and destructive actions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg border-red-200">
              <div>
                <div className="font-medium text-red-600">Reset All Settings</div>
                <div className="text-sm text-muted-foreground">
                  Reset all settings to their default values
                </div>
              </div>
              <Button 
                variant="destructive" 
                onClick={handleResetAllSettings}
                disabled={loading}
                className="flex items-center space-x-2"
              >
                <RotateCcw className="h-4 w-4" />
                <span>Reset All</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Changes */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {hasChanges && (
            <Badge variant="outline" className="text-orange-600">
              Unsaved changes
            </Badge>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          {hasChanges && (
            <Button variant="outline" onClick={handleReset}>
              Reset
            </Button>
          )}
          <Button 
            onClick={handleSave} 
            disabled={!hasChanges || loading}
            className="flex items-center space-x-2"
          >
            {loading ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>{loading ? 'Saving...' : 'Save Changes'}</span>
          </Button>
        </div>
      </div>
    </div>
  )
} 